import { queryOptions, useQuery } from '@tanstack/react-query';
import { ActivityService } from '../../generated';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { STALE_TIME_LIVE_VIEW } from '../../queryClient';

export const activityLicensePlateKeys = {
  base: () => ['activityLicensePlate'] as const,
  one: ({ jobsiteId, captureStartAt }: ActivityLicensePlateOptionProps) =>
    [...activityLicensePlateKeys.base(), jobsiteId, captureStartAt] as const,
};

export interface ActivityLicensePlateOptionProps {
  jobsiteId: string;
  captureStartAt: string;
}

const FAST_POLL_WINDOW = 1000 * 60 * 5; // poll fast for this long after capture
const FAST_POLL_INTERVAL = 1000 * 60; // 1 minute

export function activityLicensePlateOptions({
  jobsiteId,
  captureStartAt,
  enabled,
}: ActivityLicensePlateOptionProps & { enabled: boolean }) {
  return queryOptions<string, ApiError>({
    queryKey: activityLicensePlateKeys.one({ jobsiteId, captureStartAt }),
    queryFn: async () => {
      if (!jobsiteId || !captureStartAt) {
        throw new Error('jobsiteId and captureStartAt are required');
      }

      const response = await ActivityService.activityControllerGetActivityLicensePlate({
        path: {
          jobsiteId,
          capture_start_at: captureStartAt,
        },
      });

      if (response.data !== undefined) {
        return response.data.ocr_text;
      } else {
        throw (response as any).error || new Error(response?.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!captureStartAt && enabled,
    staleTime: STALE_TIME_LIVE_VIEW,
    refetchInterval: () => {
      const age = Date.now() - new Date(captureStartAt).getTime();
      return age < FAST_POLL_WINDOW ? FAST_POLL_INTERVAL : STALE_TIME_LIVE_VIEW;
    },
  });
}

export function useActivityLicensePlate(props: ActivityLicensePlateOptionProps & { enabled: boolean }) {
  return useQuery(activityLicensePlateOptions(props));
}
